import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as mock1Data from "./toefl-mock1/writingData";
import * as mock2Data from "./toefl-mock2/writingData";
import * as mock3Data from "./toefl-mock3/writingData";
import * as mock4Data from "./toefl-mock4/writingData";
import * as mock5Data from "./toefl-mock5/writingData";
import * as mock6Data from "./toefl-mock6/writingData";
import * as mock7Data from "./toefl-mock7/writingData";
import * as mock8Data from "./toefl-mock8/writingData";
import * as mock9Data from "./toefl-mock9/writingData";
import { scoreEmail, scoreDiscussion } from "./toeflWritingRubric";
import {
  C,
  TimerBadge,
  SubmittingScreen,
  useCountdown,
  usePageTop,
  useWindowWidth,
  scrollToTop,
  linearBand,
  bandSummary,
  saveToeflResult,
} from "./toeflShared";

// ============================================================
// TOEFL iBT WRITING (2026 format)
// Build a Sentence → Write an Email → Academic Discussion
// Question sets live in src/quiz/toefl-mock<N>/writingData.js
// ============================================================

const WRITING_DATA = { 1: mock1Data, 2: mock2Data, 3: mock3Data, 4: mock4Data, 5: mock5Data, 6: mock6Data, 7: mock7Data, 8: mock8Data, 9: mock9Data };

const STAGES = ["build", "email", "discussion"];
const STAGE_LABELS = { build: "Build a Sentence", email: "Write an Email", discussion: "Academic Discussion" };

// Section weighting used to turn the three task scores into one band.
const WEIGHTS = { build: 0.3, email: 0.3, discussion: 0.4 };

// Deterministic shuffle so the tiles keep their order across renders.
const distractorsOf = (item) => item.distractors || (item.distractor ? [item.distractor] : []);

function shuffledTiles(item) {
  const tiles = [...item.answer, ...distractorsOf(item)];
  let seed = item.id * 9301 + 49297;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
  }
  // Never show the answer already in order.
  if (tiles.slice(0, item.answer.length).join(" ") === item.answer.join(" ")) tiles.push(tiles.shift());
  return tiles;
}

const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;

const sentenceText = (item, words) =>
  [item.prefix, words.join(" ")].filter(Boolean).join(" ") + item.suffix;

// The keyed order or any listed alternative that is also grammatical.
const isCorrectOrder = (item, words) => {
  const given = words.join(" ").toLowerCase();
  return [item.answer, ...(item.accept || [])].some((order) => order.join(" ").toLowerCase() === given);
};

function ToeflWriting({ mock = 1 }) {
  const { buildSentenceItems, emailTask, discussionTask, TASK_SECONDS } = WRITING_DATA[mock] || mock1Data;
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;
  usePageTop();

  const [stage, setStage] = useState("build");
  const [buildIndex, setBuildIndex] = useState(0);
  const [placed, setPlaced] = useState({}); // itemId -> [tileIndex]
  const [email, setEmail] = useState("");
  const [discussion, setDiscussion] = useState("");
  const submittedRef = useRef(false);

  const tilesById = useMemo(
    () => Object.fromEntries(buildSentenceItems.map((it) => [it.id, shuffledTiles(it)])),
    [buildSentenceItems]
  );

  useEffect(() => {
    scrollToTop();
  }, [stage]);

  // ── Scoring & submission ──────────────────────────────────────
  const submitTest = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setStage("submitting");

    const review = buildSentenceItems.map((item, i) => {
      const words = (placed[item.id] || []).map((t) => tilesById[item.id][t]);
      const isCorrect = isCorrectOrder(item, words);
      return {
        number: i + 1,
        category: "Build a Sentence",
        questionText: `"${item.speaker}"`,
        userAnswerText: words.length ? sentenceText(item, words) : "No answer",
        correctAnswerText: sentenceText(item, item.answer),
        isCorrect,
      };
    });
    const buildCorrect = review.filter((r) => r.isCorrect).length;
    const buildTotal = buildSentenceItems.length;

    const emailResult = scoreEmail(email, emailTask);
    const discussionResult = scoreDiscussion(discussion, discussionTask);

    const fraction =
      WEIGHTS.build * (buildCorrect / buildTotal) +
      WEIGHTS.email * (emailResult.score / 5) +
      WEIGHTS.discussion * (discussionResult.score / 5);
    const summary = bandSummary(linearBand(fraction));

    const criteriaLines = (r) => r.criteria.map((c) => `${c.label}: ${c.score}/5 — ${c.diagnostics.join(" ")}`);
    const breakdown = {
      build: {
        label: "Build a Sentence",
        score: buildCorrect,
        max: buildTotal,
        diagnostics: [`${buildCorrect} of ${buildTotal} sentences built correctly.`],
      },
      email: {
        label: `Write an Email (${emailResult.wordCount} words)`,
        score: emailResult.score,
        max: 5,
        diagnostics: criteriaLines(emailResult),
      },
      discussion: {
        label: `Academic Discussion (${discussionResult.wordCount} words)`,
        score: discussionResult.score,
        max: 5,
        diagnostics: criteriaLines(discussionResult),
      },
    };
    const warnings = [
      ...emailResult.warnings.map((w) => `Email — ${w}`),
      ...discussionResult.warnings.map((w) => `Discussion — ${w}`),
    ];

    const saveStatus = await saveToeflResult("TOEFL Writing", {
      format: "TOEFL iBT 2026",
      mock,
      ...summary,
      compositeScore: summary.bandScore,
      buildSentence: { correct: buildCorrect, total: buildTotal },
      breakdown,
      warnings,
      review,
      essay: `EMAIL TO ${emailTask.recipient.toUpperCase()}\n${email}\n\nACADEMIC DISCUSSION\n${discussion}`,
    });

    navigate("/quiz/toefl/result", {
      state: {
        testType: "Writing",
        ...summary,
        taskScores: Object.values(breakdown),
        warnings,
        review,
        saveStatus,
      },
    });
  }, [placed, tilesById, email, discussion, navigate, mock, buildSentenceItems, emailTask, discussionTask]);

  const advance = useCallback(() => {
    const next = STAGES[STAGES.indexOf(stage) + 1];
    if (next) setStage(next);
    else submitTest();
  }, [stage, submitTest]);

  const running = STAGES.includes(stage);
  const [timeLeft, setTimeLeft] = useCountdown(TASK_SECONDS.build, running, advance);

  // Each task has its own clock.
  const prevStageRef = useRef(stage);
  useEffect(() => {
    if (prevStageRef.current !== stage && TASK_SECONDS[stage]) setTimeLeft(TASK_SECONDS[stage]);
    prevStageRef.current = stage;
  }, [stage, setTimeLeft, TASK_SECONDS]);

  const handleContinue = () => {
    let warning = null;
    if (stage === "build") {
      const unfinished = buildSentenceItems.filter((it) => !(placed[it.id] || []).length).length;
      if (unfinished) warning = `${unfinished} sentence(s) are still empty.`;
    } else if (stage === "email" && countWords(email) < 50) {
      warning = `Your email has only ${countWords(email)} words.`;
    } else if (stage === "discussion" && countWords(discussion) < 100) {
      warning = `Your response has only ${countWords(discussion)} words (aim for 100+).`;
    }
    const tail = stage === "discussion" ? "Submit the Writing section anyway?" : "You cannot return to this task. Continue anyway?";
    if (warning && !window.confirm(`${warning} ${tail}`)) return;
    advance();
  };

  // Exit ends the section early: whatever has been written is scored and
  // saved, so the score still reaches the results page and profile.
  const handleExit = () => {
    if (!window.confirm("Exit the Writing test? Your responses so far will be scored and saved. Tasks you have not reached will score zero.")) return;
    submitTest();
  };

  if (stage === "submitting") return <SubmittingScreen isMobile={isMobile} message="Scoring your writing..." />;

  const stepNumber = STAGES.indexOf(stage) + 1;

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto", padding: isMobile ? "88px 12px 40px" : "100px 24px 40px", color: "#111", boxSizing: "border-box" }}>
      {/* TOOLBAR */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px", marginBottom: "12px" }}>
        <div style={{ fontSize: isMobile ? "1.1rem" : "1.4rem", color: "#555" }}>
          TOEFL iBT / <span style={{ color: C.accent, fontWeight: 600 }}>Writing</span>{" "}
          <span style={{ fontSize: "0.8em", color: C.muted }}>· Task {stepNumber} of 3: {STAGE_LABELS[stage]}</span>
        </div>
        <TimerBadge timeLeft={timeLeft} isMobile={isMobile} />
      </div>

      {/* STEP INDICATOR */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px" }}>
        {STAGES.map((s, i) => (
          <div key={s} style={{ flex: 1 }}>
            <div style={{ height: "5px", borderRadius: "3px", background: i < stepNumber ? C.accent : "#e0f0e8" }} />
            {!isMobile && (
              <div style={{ fontSize: "0.75rem", marginTop: "4px", color: s === stage ? C.greenText : C.muted, fontWeight: s === stage ? 700 : 500 }}>
                {STAGE_LABELS[s]}
              </div>
            )}
          </div>
        ))}
      </div>

      {stage === "build" && (
        <BuildSentence
          items={buildSentenceItems}
          index={buildIndex}
          setIndex={setBuildIndex}
          placed={placed}
          setPlaced={setPlaced}
          tilesById={tilesById}
          isMobile={isMobile}
        />
      )}

      {stage === "email" && (
        <>
          <div style={promptCard(isMobile)}>
            <h2 style={promptTitle}>Write an Email</h2>
            <p style={{ margin: "0 0 10px", lineHeight: 1.7 }}>{emailTask.scenario}</p>
            <p style={{ margin: "0 0 6px", fontWeight: 600 }}>In your email, do the following:</p>
            <ul style={{ margin: 0, paddingLeft: "1.3rem", lineHeight: 1.8 }}>
              {emailTask.points.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <p style={{ margin: "10px 0 0", fontSize: "0.85rem", color: C.muted }}>
              Write as much as you can, in complete sentences. You have 7 minutes.
            </p>
          </div>
          <div style={{ fontSize: "0.9rem", color: "#555", marginBottom: "6px" }}>
            <strong>To:</strong> {emailTask.recipient}
          </div>
          <ResponseBox value={email} onChange={setEmail} placeholder={`Dear ${emailTask.recipient},\n\n`} rows={12} />
        </>
      )}

      {stage === "discussion" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
          <div>
            <div style={promptCard(isMobile)}>
              <h2 style={promptTitle}>Academic Discussion</h2>
              <p style={{ margin: "0 0 4px", fontSize: "0.85rem", color: C.muted }}>
                Your professor is teaching a class on {discussionTask.course || "education"}. Write a post responding to the professor's question.
                Express and support your opinion, and make a contribution to the discussion. An effective response
                contains at least 100 words. You have 10 minutes.
              </p>
            </div>
            <Post name={discussionTask.professor} text={discussionTask.question} professor />
            {discussionTask.posts.map((p) => <Post key={p.name} name={p.name} text={p.text} />)}
          </div>
          <div>
            <ResponseBox value={discussion} onChange={setDiscussion} placeholder="Type your response here..." rows={isMobile ? 12 : 20} />
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", gap: "12px", flexWrap: "wrap" }}>
        <button type="button" onClick={handleExit} style={ghostBtn}>
          Exit Test
        </button>
        <button type="button" onClick={handleContinue} style={primaryBtn}>
          {stage === "discussion" ? "Finish & Submit ✓" : `Next: ${STAGE_LABELS[STAGES[stepNumber]]} →`}
        </button>
      </div>
    </div>
  );
}

// ── Build a Sentence ────────────────────────────────────────────
function BuildSentence({ items, index, setIndex, placed, setPlaced, tilesById, isMobile }) {
  const item = items[index];
  const extra = distractorsOf(item).length;
  const tiles = tilesById[item.id];
  const used = placed[item.id] || [];

  const place = (tileIndex) =>
    setPlaced((prev) => ({ ...prev, [item.id]: [...(prev[item.id] || []), tileIndex] }));
  const remove = (pos) =>
    setPlaced((prev) => ({ ...prev, [item.id]: (prev[item.id] || []).filter((_, i) => i !== pos) }));
  const reset = () => setPlaced((prev) => ({ ...prev, [item.id]: [] }));

  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: "14px", padding: isMobile ? "16px" : "24px 28px" }}>
      <p style={{ margin: "0 0 16px", fontSize: "0.9rem", color: C.muted }}>
        Click the words in the right order to complete the reply. Click a placed word to remove it.
        {extra === 1 && " One word is not needed."}
        {extra > 1 && ` ${extra} words are not needed.`}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
        <div style={{ ...bubble, alignSelf: "flex-start", background: C.soft }}>
          <strong style={{ color: C.greenText }}>A:</strong> {item.speaker}
        </div>
        <div style={{ ...bubble, alignSelf: "flex-end", background: "#fff", border: `1.5px dashed ${C.accent}`, minHeight: "48px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
          <strong style={{ color: C.greenText }}>B:</strong>
          {item.prefix && <span>{item.prefix}</span>}
          {used.length === 0 && <span style={{ color: "#aaa" }}>_____ _____ _____</span>}
          {used.map((t, pos) => (
            <button key={`${t}-${pos}`} type="button" onClick={() => remove(pos)} style={{ ...tile, background: "#e8f5ee", borderColor: C.accent }} title="Remove">
              {tiles[t]}
            </button>
          ))}
          <span>{item.suffix}</span>
        </div>
        {/* Shows how many tiles the reply needs, so a sentence that is
            grammatical but leaves out a required word isn't submitted by accident. */}
        <div
          style={{
            alignSelf: "flex-end",
            fontSize: "0.8rem",
            fontWeight: 600,
            color: used.length === item.answer.length ? C.greenText : used.length > item.answer.length ? "#b42318" : C.muted,
          }}
        >
          Words placed: {used.length} of {item.answer.length}
          {used.length > 0 && used.length < item.answer.length && ` — ${item.answer.length - used.length} more needed`}
          {used.length > item.answer.length && " — too many words"}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center", padding: "14px", background: C.softer, borderRadius: "12px", border: `1px solid ${C.border}` }}>
        {tiles.map((word, t) => {
          const isUsed = used.includes(t);
          return (
            <button
              key={t}
              type="button"
              disabled={isUsed}
              onClick={() => place(t)}
              style={{ ...tile, opacity: isUsed ? 0.3 : 1, cursor: isUsed ? "default" : "pointer" }}
            >
              {word}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", flexWrap: "wrap", gap: "10px" }}>
        <button type="button" onClick={reset} style={{ ...ghostBtn, fontSize: "0.82rem" }}>
          Clear
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <button type="button" disabled={index === 0} onClick={() => setIndex(index - 1)} style={{ ...ghostBtn, opacity: index === 0 ? 0.4 : 1 }}>
            ← Prev
          </button>
          {items.map((it, i) => {
            const done = (placed[it.id] || []).length > 0;
            const current = i === index;
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => setIndex(i)}
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  padding: 0,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: current ? C.accent : done ? "#cfeedd" : "#fff",
                  color: current ? "#fff" : C.accent,
                  border: `1.5px solid ${current || done ? C.accent : C.border}`,
                }}
              >
                {i + 1}
              </button>
            );
          })}
          <button
            type="button"
            disabled={index === items.length - 1}
            onClick={() => setIndex(index + 1)}
            style={{ ...ghostBtn, opacity: index === items.length - 1 ? 0.4 : 1 }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function Post({ name, text, professor }) {
  return (
    <div style={{ background: professor ? C.soft : "#fff", border: `1px solid ${C.border}`, borderRadius: "12px", padding: "12px 16px", marginBottom: "10px" }}>
      <div style={{ fontWeight: 700, color: C.greenText, marginBottom: "4px", fontSize: "0.9rem" }}>
        {professor ? `👩‍🏫 ${name}` : `🧑‍🎓 ${name}`}
      </div>
      <p style={{ margin: 0, lineHeight: 1.7, fontSize: "0.93rem" }}>{text}</p>
    </div>
  );
}

function ResponseBox({ value, onChange, placeholder, rows }) {
  return (
    <>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        style={{
          width: "100%",
          padding: "1rem",
          border: "1px solid #b2d8c2",
          borderRadius: "10px",
          fontSize: "1rem",
          lineHeight: 1.7,
          resize: "vertical",
          outline: "none",
          boxSizing: "border-box",
          background: "#fff",
          color: "#111",
          fontFamily: "inherit",
        }}
      />
      <div style={{ textAlign: "right", fontSize: "0.85rem", color: C.muted, marginTop: "4px" }}>
        Words: <strong style={{ color: C.greenText }}>{countWords(value)}</strong>
      </div>
    </>
  );
}

// ── Styles ──────────────────────────────────────────────────────
const promptCard = (isMobile) => ({
  background: C.softer,
  border: `1px solid ${C.border}`,
  borderRadius: "12px",
  padding: isMobile ? "14px" : "18px 22px",
  marginBottom: "14px",
});

const promptTitle = { color: C.greenText, margin: "0 0 8px", fontSize: "1.15rem" };

const bubble = {
  maxWidth: "92%",
  padding: "12px 16px",
  borderRadius: "14px",
  border: `1px solid ${C.border}`,
  fontSize: "1rem",
  lineHeight: 1.6,
  boxSizing: "border-box",
};

const tile = {
  padding: "6px 12px",
  border: `1.5px solid ${C.border}`,
  borderRadius: "8px",
  background: "#fff",
  color: "#111",
  fontSize: "0.95rem",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtn = {
  padding: "8px 18px",
  background: "transparent",
  border: `1px solid ${C.border}`,
  color: "#555",
  borderRadius: "30px",
  cursor: "pointer",
  fontSize: "0.88rem",
};

const primaryBtn = {
  padding: "11px 28px",
  background: C.accent,
  color: "#fff",
  border: "none",
  borderRadius: "30px",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "0.95rem",
};

export default ToeflWriting;
